import { useSnackbar } from '@hue/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, CalendarDays, LoaderCircle, UsersRound } from 'lucide-react';
import { businessUnitApi, getApiError } from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import { PageHeader } from './page-title.js';

/// Agenda e Profissionais são de uma unidade: na visão consolidada, ou numa unidade de apoio, pedem
/// antes um estabelecimento (ADR 0039).
export function ChooseSchedulingUnit({
  subject,
  units,
  reason,
}: {
  subject: 'agenda' | 'professionals';
  units: ReadonlyArray<{ id: string; displayName: string }>;
  reason: string;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const choose = useMutation({
    mutationFn: businessUnitApi.switch,
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      await refreshSession(queryClient);
    },
    onError: (error) => snackbar.failure(getApiError(error)),
  });
  const agenda = subject === 'agenda';
  const Icon = agenda ? CalendarDays : UsersRound;
  return (
    <section aria-labelledby="scheduling-unit-title">
      <PageHeader
        trail={agenda ? 'Operação / Agenda' : 'Operação / Profissionais'}
        id="scheduling-unit-title"
        title={agenda ? 'Agenda' : 'Profissionais'}
        description={<>{reason}</>}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {units.map((unit) => (
          // O card inteiro abre a tela: mirar só no ícone era alvo pequeno demais.
          <button
            key={unit.id}
            type="button"
            aria-label={`${agenda ? 'Abrir agenda da' : 'Abrir profissionais da'} ${unit.displayName}`}
            aria-busy={(choose.isPending && choose.variables === unit.id) || undefined}
            disabled={choose.isPending}
            onClick={() => choose.mutate(unit.id)}
            className="hue-card flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-surface-border bg-surface-card p-5 text-left shadow-card transition enabled:hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                <Building2 className="size-5" />
              </span>
              <span className="truncate font-semibold">{unit.displayName}</span>
            </span>
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center text-content-faint"
            >
              {choose.isPending && choose.variables === unit.id ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Icon className="size-4" />
              )}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
